import { Extension } from "@tiptap/core";

import { focusFirstNodeAfterPos } from "@/components/tiptap/utils/focusFirstNodeAfterTitle";

import type { DocumentModel } from "@/models/DocumentModel";

export type LineNumbersStorage = {
  handleHashChange: () => void;
};

interface LineNumbersOptions {
  doc?: DocumentModel;
}

export function handleLineNumberClick(e: Event | React.MouseEvent): void {
  if (e.currentTarget) {
    e.preventDefault();
    e.stopPropagation();
    const allLines = Array.from(document.querySelectorAll(".line-number"));
    const index = allLines.indexOf(e.currentTarget as Element);
    const lineNumber = index === -1 ? null : index + 1;

    if (lineNumber) {
      const newHash = `#L${lineNumber}`;

      // Open in new tab with Cmd
      if ((e as MouseEvent).metaKey || (e as MouseEvent).ctrlKey) {
        const newUrl = `${window.location.origin}${window.location.pathname}${newHash}`;
        window.open(newUrl, "_blank");
      } else {
        window.location.hash = newHash;
      }
    }
  }
}

/**
 * Add line numbers to the top level block elements.
 * It will recalculate line numbers if number of lines is changed on transaction.
 * These line numbers are used in renderLineWrapperHTML and LineWrapper.
 */
export const LineNumbers = Extension.create<LineNumbersOptions, LineNumbersStorage>({
  name: "lineNumbers",

  addStorage() {
    return {
      handleHashChange: () => {},
    };
  },

  onCreate() {
    const { editor, storage } = this;
    const { doc } = this.options;

    const handleHashChange = () => {
      const m = window.location.hash.match(/^#L(\d+)$/);
      if (m) {
        const lineNumber = parseInt(m[1], 10);
        const anchors = editor.view.dom.getElementsByClassName("line-number");
        const anchor = anchors[lineNumber - 1];
        if (anchor) {
          if (doc) {
            const pos = editor.view.posAtDOM(anchor, 0);
            focusFirstNodeAfterPos(pos, editor, doc);
          }

          anchor.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
    };

    storage.handleHashChange = handleHashChange;
    window.addEventListener("hashchange", handleHashChange);

    queueMicrotask(() => {
      handleHashChange();
    });
  },

  onDestroy() {
    window.removeEventListener("hashchange", this.storage.handleHashChange);
  },
});
